import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GitBranch, Search, X, Edit2, Plus, Trash2, ChevronRight, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { CPTreeNode } from './CPTreeNode';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BRAND = '#1a3c2e';

function getLevelColor(level) {
  const map = { DM: '#1a3c2e', ADM: '#1565c0', CP1: '#2e7d32', CP2: '#e65100', CP3: '#6a1b9a', CP4: '#00695c', CP5: '#ad1457', CP6: '#4527a0' };
  const bg = map[level] || '#37474f';
  return { bg: bg, light: bg + '22', text: bg };
}

export default function TeamHierarchy() {
  let user = {};
  try { user = JSON.parse(localStorage.getItem('crm_user') || '{}'); } catch (e) { user = {}; }
  const isMarketing = user && user.department === 'marketing';

  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDMs, setExpandedDMs] = useState({});
  const [forceOpen, setForceOpen] = useState(0);
  const [editMode, setEditMode] = useState(false);

  // Search
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const dropRef = useRef(null);

  // Edit modals
  const [addModal, setAddModal] = useState(null);
  const [removeModal, setRemoveModal] = useState(null);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const fetchHierarchy = useCallback(async function() {
    setLoading(true);
    try {
      const r = await axios.get(API + '/cp-tracker/hierarchy');
      setHierarchy(r.data.hierarchy || []);
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(function() { fetchHierarchy(); }, [fetchHierarchy]);

  useEffect(function() {
    if (!query || query.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async function() {
      try {
        const r = await axios.get(API + '/cp-tracker/name-suggestions', { params: { q: query } });
        setSuggestions(r.data.suggestions || []);
        setShowSug(true);
      } catch (e) { setSuggestions([]); }
    }, 250);
    return function() { clearTimeout(t); };
  }, [query]);

  useEffect(function() {
    function h(e) { if (dropRef.current && !dropRef.current.contains(e.target)) setShowSug(false); }
    document.addEventListener('mousedown', h);
    return function() { document.removeEventListener('mousedown', h); };
  }, []);

  const showToast = function(msg) { setToast(msg); setTimeout(function() { setToast(''); }, 3000); };
  const toggleDM = function(n) { setExpandedDMs(function(p) { return Object.assign({}, p, { [n]: !p[n] }); }); };

  const handleSearch = async function(name) {
    const q = (name || query).trim();
    if (!q) return;
    setQuery(q); setShowSug(false); setSuggestions([]);
    setSearching(true); setNotFound(false); setResult(null);
    try {
      const [upper, down] = await Promise.all([
        axios.get(API + '/cp-tracker/upper-line', { params: { name: q } }),
        axios.get(API + '/cp-tracker/down-line', { params: { name: q } }),
      ]);
      if (upper.data.found) {
        const chain = upper.data.chain || [];
        setResult({ name: chain[0] ? chain[0].name : q, level: chain[0] ? chain[0].level : '', dm: chain[chain.length - 1] ? chain[chain.length - 1].name : '', upperChain: [...chain].reverse(), downData: down.data.found ? down.data : null });
      } else setNotFound(true);
    } catch (e) { setNotFound(true); }
    setSearching(false);
  };

  const clearSearch = function() { setQuery(''); setResult(null); setNotFound(false); setSuggestions([]); };

  const handleAdd = async function() {
    if (!newName.trim() || !addModal) return;
    setSaving(true);
    try {
      await axios.post(API + '/cp-tracker/hierarchy/add', { parent_name: addModal.parentName, new_name: newName.trim(), dm_name: addModal.dmName });
      showToast('Added "' + newName.trim() + '"');
      setAddModal(null); setNewName(''); fetchHierarchy();
    } catch (e) { showToast('Error: ' + (e.response ? e.response.data.detail : 'Failed')); }
    setSaving(false);
  };

  const handleRemove = async function() {
    if (!removeModal) return;
    setSaving(true);
    try {
      await axios.delete(API + '/cp-tracker/hierarchy/remove', { data: { name: removeModal.name, dm_name: removeModal.dmName } });
      showToast('Removed "' + removeModal.name + '"');
      setRemoveModal(null); fetchHierarchy();
    } catch (e) { showToast('Error: ' + (e.response ? e.response.data.detail : 'Failed')); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND }}></div></div>;

  return (
    <div className="space-y-4 p-1">
      {toast && <div className="fixed top-5 right-5 z-50 bg-stone-900 text-white text-sm px-4 py-2.5 rounded-xl shadow-lg">{toast}</div>}

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: BRAND }}>
          <GitBranch className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-black text-stone-900">Team Hierarchy</h1>
          <p className="text-stone-500 text-sm">Marketing · DM → ADM → CP Structure</p>
        </div>
        {isMarketing && (
          <button onClick={function() { setEditMode(function(e) { return !e; }); }}
            className={'flex items-center gap-1.5 text-sm px-4 py-2 rounded-xl font-semibold transition-colors ml-auto ' + (editMode ? 'bg-amber-500 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200')}
            data-testid="hierarchy-edit-toggle">
            <Edit2 className="w-4 h-4" />
            {editMode ? 'Edit Mode ON' : 'Edit Mode'}
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm" ref={dropRef}>
        <div className="flex gap-3 relative">
          <div className="flex-1 relative">
            <div className="flex items-center gap-2 border border-stone-300 rounded-lg px-3 py-2.5 focus-within:ring-2 bg-white">
              <Search className="w-4 h-4 text-stone-400 flex-shrink-0" />
              <input
                className="flex-1 text-sm outline-none text-stone-800 placeholder-stone-400"
                placeholder="Search DM / ADM / CP name to see upper line + down line..."
                value={query}
                onChange={function(e) { setQuery(e.target.value); setShowSug(true); }}
                onKeyDown={function(e) { if (e.key === 'Enter') handleSearch(); if (e.key === 'Escape') setShowSug(false); }}
                onFocus={function() { if (suggestions.length > 0) setShowSug(true); }}
                data-testid="team-hierarchy-search"
                autoComplete="off"
              />
              {query && <button onClick={clearSearch} className="text-stone-300 hover:text-stone-500"><X className="w-3.5 h-3.5" /></button>}
            </div>
            {showSug && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-stone-200 shadow-xl z-50 overflow-hidden max-h-52 overflow-y-auto">
                {suggestions.map(function(s, i) {
                  const sn = typeof s === 'string' ? s : s.name;
                  const sl = typeof s === 'string' ? '' : s.level;
                  const clr = getLevelColor(sl);
                  return (
                    <button key={i} onClick={function() { handleSearch(sn); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-stone-50 text-left transition-colors">
                      {sl && <span className="text-xs font-bold px-1.5 py-0.5 rounded text-white flex-shrink-0" style={{ background: clr.bg }}>{sl}</span>}
                      <span className="text-sm text-stone-700">{sn}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button onClick={function() { handleSearch(); }} disabled={searching || !query}
            className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50"
            style={{ background: BRAND }} data-testid="team-hierarchy-search-btn">
            {searching ? 'Searching…' : 'Search'}
          </button>
          {result && <button onClick={clearSearch} className="px-4 py-2.5 rounded-lg text-sm font-medium bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> Clear</button>}
        </div>
      </div>

      {/* Not found */}
      {notFound && !searching && (
        <div className="bg-white rounded-xl border border-red-200 p-6 text-center shadow-sm">
          <p className="text-stone-500 text-sm">No match for <strong>"{query}"</strong>. Try a partial name.</p>
        </div>
      )}

      {/* Search Result: Upper Line + Down Line */}
      {result && !searching && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-3" style={{ background: BRAND + '11' }}>
            <span className="text-sm font-bold text-stone-800">Structure for:</span>
            <span className="font-bold text-xs px-2 py-0.5 rounded text-white" style={{ background: getLevelColor(result.level).bg }}>{result.level}</span>
            <span className="font-bold text-stone-900">{result.name}</span>
            <span className="text-stone-400 text-xs ml-auto">DM: {result.dm || '—'}</span>
          </div>

          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-stone-100">
            {/* Upper Line */}
            <div className="p-5">
              <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">Upper Line</p>
              <div className="flex flex-col items-start gap-0.5">
                {result.upperChain.map(function(item, i) {
                  const clr = getLevelColor(item.level);
                  const isSearched = i === result.upperChain.length - 1;
                  return (
                    <div key={i} className="flex flex-col items-start w-full">
                      <div className="flex items-center gap-3 rounded-xl px-4 py-2.5 border-2 w-full"
                        style={{ borderColor: isSearched ? clr.bg : clr.light, background: isSearched ? clr.bg + '15' : '#fafaf9' }}>
                        <span className="text-xs font-bold px-2 py-0.5 rounded text-white flex-shrink-0" style={{ background: clr.bg }}>{item.level}</span>
                        <span className={'text-sm flex-1 ' + (isSearched ? 'font-bold text-stone-900' : 'font-medium text-stone-600')}>{item.name}</span>
                        {isSearched && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: clr.bg + '22', color: clr.bg }}>Searched</span>}
                      </div>
                      {i < result.upperChain.length - 1 && (
                        <div className="ml-6 py-0.5"><ChevronDown className="w-3.5 h-3.5 text-stone-300" /></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Down Line */}
            <div className="p-5">
              <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">Down Line</p>
              {result.downData && result.downData.node && result.downData.node.children && result.downData.node.children.length > 0 ? (
                <div className="space-y-1">
                  {result.downData.node.children.map(function(child, i) {
                    return React.createElement(CPTreeNode, {
                      key: i, node: child, depth: 1, forceOpen: 1,
                      canEdit: editMode && isMarketing, dmName: result.dm,
                      onAddChild: (editMode && isMarketing) ? function(pN, dmN) { setAddModal({ parentName: pN, dmName: dmN }); setNewName(''); } : null,
                      onRemove: (editMode && isMarketing) ? function(nN, dmN) { setRemoveModal({ name: nN, dmName: dmN }); } : null,
                    });
                  })}
                </div>
              ) : (
                <p className="text-xs text-stone-400 py-4 text-center">No one is under {result.name} (leaf node)</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full Hierarchy Tree */}
      {!result && !notFound && (
        <>
          <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {[{ label: 'DM', bg: '#1a3c2e' }, { label: 'ADM', bg: '#1565c0' }, { label: 'CP1', bg: '#2e7d32' }, { label: 'CP2', bg: '#e65100' }, { label: 'CP3', bg: '#6a1b9a' }].map(function(l) {
                return <span key={l.label} className="text-xs font-bold px-2 py-1 rounded text-white" style={{ background: l.bg }}>{l.label}</span>;
              })}
              <span className="text-xs text-stone-400 mx-1">→ deeper levels continue</span>
            </div>
            <div className="ml-auto flex gap-2">
              {isMarketing && editMode && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-lg font-medium">Hover rows to Add/Remove</span>}
              <button onClick={function() { setForceOpen(1); }} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 transition-colors">Expand All</button>
              <button onClick={function() { setForceOpen(0); }} className="text-xs px-3 py-1.5 rounded-lg bg-stone-100 text-stone-600 font-medium hover:bg-stone-200 transition-colors">Collapse All</button>
            </div>
          </div>

          {hierarchy.map(function(dm) {
            const isOpen = expandedDMs[dm.name];
            const isTeamGroup = !!dm.teamLead;
            return (
              <div key={dm.name} className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
                <div className="flex items-center px-5 py-4 hover:bg-emerald-50 transition-colors cursor-pointer" onClick={function() { toggleDM(dm.name); }} data-testid={'dm-row-' + dm.name}>
                  <div className="flex items-center gap-3 flex-1">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />}
                    {isTeamGroup ? (
                      <div className="w-12 h-9 rounded-lg flex items-center justify-center text-white text-[10px] font-bold leading-tight px-1 text-center" style={{ background: '#065f46' }}>TEAM</div>
                    ) : (
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ background: BRAND }}>DM</div>
                    )}
                    <div>
                      <p className="font-semibold text-stone-800">{dm.name}</p>
                      {isTeamGroup ? (
                        <p className="text-xs text-stone-400">
                          <span className="font-medium text-emerald-700">TL: {dm.teamLead}</span>
                          {' · '}{dm.admCount || 0} DMs · {dm.cpCount || 0}+ members
                        </p>
                      ) : (
                        <p className="text-xs text-stone-400">{dm.admCount || 0} ADMs · {dm.cpCount || 0}+ CPs</p>
                      )}
                    </div>
                  </div>
                  {isMarketing && editMode && (
                    <button onClick={function(e) { e.stopPropagation(); setAddModal({ parentName: dm.name, dmName: dm.name }); setNewName(''); }}
                      className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 font-semibold px-2 py-1 rounded hover:bg-emerald-50">
                      <Plus className="w-3.5 h-3.5" /> Add ADM
                    </button>
                  )}
                </div>
                {isOpen && (
                  <div className="border-t border-stone-100 px-5 py-3 space-y-1">
                    {(dm.children || dm.adms || []).length === 0 ? <p className="text-stone-400 text-sm italic py-2">{isTeamGroup ? 'No DMs' : 'No ADMs'}</p> : (
                      (dm.children || dm.adms || []).map(function(adm, i) {
                        return React.createElement(CPTreeNode, {
                          key: i, node: adm, depth: 0, forceOpen: forceOpen,
                          canEdit: editMode && isMarketing, dmName: dm.name,
                          isTeamGroup: isTeamGroup,
                          onAddChild: (editMode && isMarketing) ? function(pN, dmN) { setAddModal({ parentName: pN, dmName: dmN }); setNewName(''); } : null,
                          onRemove: (editMode && isMarketing) ? function(nN, dmN) { setRemoveModal({ name: nN, dmName: dmN }); } : null,
                        });
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {isMarketing && editMode && (
            <button onClick={function() { setAddModal({ parentName: null, dmName: null, isDM: true }); setNewName(''); }}
              className="flex items-center gap-2 text-sm text-emerald-600 font-semibold bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 w-full justify-center hover:bg-emerald-100 transition-colors">
              <Plus className="w-4 h-4" /> Add New DM
            </button>
          )}
        </>
      )}

      {/* Add Modal */}
      {addModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h3 className="font-bold text-stone-900 text-lg mb-1">Add Member</h3>
            <p className="text-stone-500 text-sm mb-4">Under: <strong>{addModal.parentName || 'Root'}</strong></p>
            <input className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 mb-4" style={{ '--tw-ring-color': BRAND }}
              placeholder="Full name…" value={newName} onChange={function(e) { setNewName(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Enter') handleAdd(); }} autoFocus />
            <div className="flex gap-2">
              <button onClick={handleAdd} disabled={!newName.trim() || saving} className="flex-1 py-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50" style={{ background: BRAND }}>
                {saving ? 'Saving…' : 'Add'}
              </button>
              <button onClick={function() { setAddModal(null); setNewName(''); }} className="flex-1 bg-stone-100 text-stone-700 py-2 rounded-lg text-sm font-semibold hover:bg-stone-200 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Modal */}
      {removeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h3 className="font-bold text-stone-900 text-lg mb-1">Remove Member</h3>
            <p className="text-stone-500 text-sm mb-4">Remove <strong className="text-red-600">{removeModal.name}</strong>? This removes all their downline too.</p>
            <div className="flex gap-2">
              <button onClick={handleRemove} disabled={saving} className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors">{saving ? 'Removing…' : 'Remove'}</button>
              <button onClick={function() { setRemoveModal(null); }} className="flex-1 bg-stone-100 text-stone-700 py-2 rounded-lg text-sm font-semibold hover:bg-stone-200 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
